import React from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isWithinInterval,
} from "date-fns";
import { it } from "date-fns/locale";
import "./MiniCalendario.css";

const MiniCalendario = ({
  presenze,
  timbrature,
  reperibilita, // Add reperibilita prop
  monthFilter,
  onDayClick,
  onReperibilitaClick, // Add onReperibilitaClick prop
}) => {
  const getTimbraturaDay = (timbratura) => {
    if (!timbratura) return null;

    if (timbratura.ingressoDate instanceof Date) {
      return timbratura.ingressoDate;
    }

    if (typeof timbratura.dataRiferimento === "string" && timbratura.dataRiferimento) {
      const parsed = new Date(`${timbratura.dataRiferimento}T12:00:00`);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const legacyDate = timbratura.dataRegistrazione?.toDate?.();
    return legacyDate || null;
  };

  // Get the month date from monthFilter
  const monthDate = new Date(monthFilter);
  const startDate = startOfMonth(monthDate);
  const endDate = endOfMonth(monthDate);
  const daysInMonth = eachDayOfInterval({ start: startDate, end: endDate });

  // Function to determine the status of a day
  const getDayStatus = (day) => {
    // Check if there's a presenza for this day
    const hasPresenza = presenze?.some((presenza) => {
      const presenzaDate = presenza.dataRegistrazione?.toDate();
      return presenzaDate && isSameDay(presenzaDate, day);
    });

    // Check if there's a timbratura for this day
    const hasTimbratura = timbrature?.some((timbratura) => {
      const timbraturaDate = getTimbraturaDay(timbratura);
      return timbraturaDate && isSameDay(timbraturaDate, day);
    });

    // Determine status:
    // Green: Has presenza (regardless of timbratura)
    // Yellow: Has only timbratura (no presenza)
    // Red: Has neither presenza nor timbratura
    // Default: No color for future dates or weekends

    const today = new Date();
    const isToday = isSameDay(day, today);
    const isPast = day < today;
    const isWeekend = day.getDay() === 0 || day.getDay() === 6; // Sunday = 0, Saturday = 6

    // Only show status for past days and today (excluding weekends)
    if (!isPast && !isToday) return "future";
    if (isWeekend) return "weekend";

    if (hasPresenza) return "complete"; // Green: presenza compilata (indipendentemente dalla timbratura)
    if (hasTimbratura) return "partial"; // Yellow: solo timbratura (nessuna presenza)
    return "absent"; // Red: mancano sia presenza che timbratura
  };

  // Helper to check reperibilita
  const getReperibilita = (day) => {
    return reperibilita?.find((rep) => {
      const start = rep.dataInizio?.toDate();
      const end = rep.dataFine?.toDate();
      if (!start || !end) return false;

      // Normalize dates to ignore time for day comparison
      const checkDay = new Date(day);
      checkDay.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues

      const startDay = new Date(start);
      startDay.setHours(0, 0, 0, 0);

      const endDay = new Date(end);
      endDay.setHours(23, 59, 59, 999);

      return isWithinInterval(checkDay, { start: startDay, end: endDay });
    });
  };

  // Get day names for header
  const dayNames = ["L", "M", "M", "G", "V", "S", "D"]; // Italian day abbreviations

  // Calculate starting day of week (Monday = 0)
  const firstDayOfMonth = startDate.getDay();
  const startingDayIndex = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1; // Convert Sunday (0) to 6

  // Create empty cells for days before the first day of the month
  const emptyCells = Array(startingDayIndex).fill(null);

  return (
    <div className="mini-calendario">
      <div className="calendario-header">
        <h4>{format(monthDate, "MMMM yyyy", { locale: it })}</h4>
      </div>

      <div className="calendario-grid">
        {/* Day names header */}
        {dayNames.map((dayName, index) => (
          <div key={`day-name-${index}`} className="day-name">
            {dayName}
          </div>
        ))}

        {/* Empty cells for days before first day of month */}
        {emptyCells.map((_, index) => (
          <div key={`empty-${index}`} className="day-cell empty"></div>
        ))}

        {/* Days of the month */}
        {daysInMonth.map((day) => {
          const status = getDayStatus(day);
          const dayNumber = format(day, "d");
          const rep = getReperibilita(day);

          return (
            <div
              key={format(day, "yyyy-MM-dd")}
              className={`day-cell ${status} ${onDayClick ? "clickable" : ""}`}
              title={`${format(day, "dd/MM/yyyy")} - ${getStatusText(status)}${
                rep ? " - Reperibilità" : ""
              }`}
              onClick={() => onDayClick && onDayClick(day)}
              style={{ position: "relative" }}
            >
              {dayNumber}
              {rep && (
                <div
                  className="reperibilita-indicator"
                  title="Reperibilità"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onReperibilitaClick) {
                      onReperibilitaClick(rep);
                    }
                  }}
                ></div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legenda */}
      <div className="calendar-legend">
        <div className="legend-item">
          <span className="legend-color complete"></span>
          <span>Presenza compilata</span>
        </div>
        <div className="legend-item">
          <span className="legend-color partial"></span>
          <span>Solo timbratura</span>
        </div>
        <div className="legend-item">
          <span className="legend-color absent"></span>
          <span>Assente</span>
        </div>
        <div className="legend-item">
          <span className="legend-color weekend"></span>
          <span>Weekend</span>
        </div>
        <div className="legend-item">
          <span className="legend-color reperibilita"></span>
          <span>Reperibilità</span>
        </div>
      </div>
    </div>
  );
};

// Helper function to get status text for tooltip
const getStatusText = (status) => {
  switch (status) {
    case "complete":
      return "Presenza completa (Presenza + Timbratura)";
    case "partial":
      return "Solo timbratura";
    case "absent":
      return "Assente";
    case "weekend":
      return "Weekend";
    case "future":
      return "Giorno futuro";
    default:
      return "";
  }
};

export default MiniCalendario;
